import { ItemArt } from '@/shared/ui/item-art';
import { gameColors, metalPanel } from '@/shared/ui/game-theme';
import { GameIcon, type GameIconName } from '@/shared/ui/game-icon';
import type { CSSProperties } from 'react';
import type { EquipmentItemResponse } from '@/network/hero.api';
import { RARITY_COLORS, RARITY_GLOW } from '@/shared/constants';
import { useT, useLocale } from '@/i18n';
import { displayName } from '@/i18n/displayName';
import type { Translations } from '@/i18n';

/**
 * Grid layout (row-major):
 *   finger | head | neck
 *   weapon | chest | cloak
 *   hands  | legs  | wrist
 *     .    | feet  |   .
 */
const SLOT_LAYOUT: Array<{
  key: string;
  icon: GameIconName;
  labelKey: keyof Translations;
  area: 'finger' | 'head' | 'neck' | 'weapon' | 'chest' | 'cloak' | 'hands' | 'wrist' | 'feet' | 'legs' | 'offhand' | 'quiver';
}> = [
  { key: 'finger', icon: 'ring', labelKey: 'slotRing', area: 'finger' },
  { key: 'head', icon: 'helmet', labelKey: 'slotHead', area: 'head' },
  { key: 'neck', icon: 'gem', labelKey: 'slotNeck', area: 'neck' },
  { key: 'main_hand', icon: 'sword', labelKey: 'slotWeapon', area: 'weapon' },
  { key: 'off_hand', icon: 'shield', labelKey: 'slotOffHand', area: 'offhand' },
  { key: 'quiver', icon: 'backpack', labelKey: 'slotQuiver', area: 'quiver' },
  { key: 'chest', icon: 'shirt', labelKey: 'slotChest', area: 'chest' },
  { key: 'cloak', icon: 'shield', labelKey: 'slotCloak', area: 'cloak' },
  { key: 'hands', icon: 'hand', labelKey: 'slotHands', area: 'hands' },
  { key: 'wrist', icon: 'link', labelKey: 'slotWrist', area: 'wrist' },
  { key: 'feet', icon: 'footprints', labelKey: 'slotFeet', area: 'feet' },
  { key: 'legs', icon: 'legs', labelKey: 'slotLegs', area: 'legs' },
];

const dollWrap: CSSProperties = {
  display: 'grid',
  gridTemplateAreas: `
    "finger head neck"
    "weapon chest offhand"
    "hands legs wrist"
    "cloak feet quiver"
  `,
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr)',
  gap: 6,
  alignItems: 'stretch',
  justifyItems: 'center',
  padding: '8px 4px',
  minHeight: 300,
};

const chestBackdrop: CSSProperties = {
  gridArea: 'chest',
  width: '100%',
  maxWidth: 132,
  minHeight: 120,
  borderRadius: 4,
  background: 'linear-gradient(180deg, #77614b24, #17191966)',
  border: '1px solid rgba(255,255,255,0.1)',
  boxShadow: 'inset 0 -24px 48px rgba(0,0,0,0.4)',
  zIndex: 0,
  pointerEvents: 'none',
  alignSelf: 'stretch',
  justifySelf: 'center',
};

const slotBox: CSSProperties = {
  width: '100%',
  minWidth: 0,
  maxWidth: 96,
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.14)',
  backgroundColor: 'rgba(0,0,0,0.42)',
  padding: '6px 6px',
  fontSize: 12,
  color: gameColors.muted,
  textAlign: 'center',
  zIndex: 1,
  position: 'relative',
  ...metalPanel,
};

const slotLabel: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: gameColors.muted,
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

const iconRow: CSSProperties = {
  fontSize: 14,
  marginBottom: 2,
};

const itemNameStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 12,
  lineHeight: 1.25,
  wordBreak: 'break-word',
};

const statTiny: CSSProperties = {
  fontSize: 11,
  color: gameColors.faint,
  marginTop: 2,
};

function rarityColor(rarity: string): string {
  return RARITY_COLORS[rarity.toLowerCase()] ?? '#9d9d9d';
}

function rarityGlow(rarity: string): string {
  return RARITY_GLOW[rarity.toLowerCase()] ?? 'none';
}

function statLabel(statType: string, tr: Translations): string {
  switch (statType) {
    case 'attack': return tr.atk;
    case 'defense': return tr.def;
    case 'speed': return tr.spd;
    default: return tr.stat;
  }
}

interface EquipmentPaperDollProps {
  equipment: Record<string, Omit<EquipmentItemResponse, 'basePrimary'>>;
  onSelectItem?: (itemId: number, trigger: HTMLElement) => void;
  selectedId?: number | null;
}

export function EquipmentPaperDoll({ equipment, onSelectItem, selectedId }: EquipmentPaperDollProps) {
  const tr = useT();
  const { locale } = useLocale();
  return (
    <div style={dollWrap} className="th-equipment-doll">
      <div style={chestBackdrop} aria-hidden />

      {SLOT_LAYOUT.map((def) => {
        const item = equipment?.[def.key];
        const gridArea = def.area;

        if (!item) {
          return (
            <div key={def.key} data-equipment-slot={def.key} style={{ ...slotBox, gridArea }}>
              <div style={slotLabel}>{tr[def.labelKey]}</div>
              <div style={iconRow}><GameIcon name={def.icon} size={18} color={gameColors.copper} /></div>
              <div style={{ color: gameColors.faint, fontStyle: 'italic', fontSize: 11 }}>{tr.empty}</div>
            </div>
          );
        }

        const color = rarityColor(item.rarity);
        const glow = rarityGlow(item.rarity);

        return (
          <button
            type="button"
            onClick={event => onSelectItem?.(item.id, event.currentTarget)}
            aria-label={`${tr[def.labelKey]}: ${displayName(locale, 'item', item)}`}
            aria-pressed={selectedId === item.id}
            key={def.key}
            data-equipment-slot={def.key}
            style={{
              ...slotBox,
              gridArea,
              cursor: 'pointer',
              borderColor: `${color}55`,
              boxShadow:
                glow !== 'none'
                  ? `0 0 10px ${color}33, inset 0 0 8px ${color}18`
                  : `inset 0 0 6px ${color}15`,
            }}
          >
            <div style={slotLabel}>{tr[def.labelKey]}</div>
            <div style={iconRow}><ItemArt item={item} size={60} /></div>
            <div style={{ ...itemNameStyle, color }}>{displayName(locale, 'item', item)}</div>
            <div style={statTiny}>
              {item.rarity} · {statLabel(item.statType, tr)} {item.primaryStat} · ilvl {item.ilvl}
            </div>
          </button>
        );
      })}
    </div>
  );
}
